// src/context/FirstRunContext.jsx
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { fetchMeasurementVariantsPresence } from "../utils/firebase/fetchMeasurementVariantsPresence";

export const FIRST_RUN_SESSION_KEY_PREFIX = "pdx.firstRun.v1";
export const FIRST_RUN_TTL_MS = 10 * 60 * 1000;

const FirstRunContext = createContext(null);

function isFresh(entry, ttlMs) {
  if (!entry?.fetchedAtMs) return false;
  return Date.now() - entry.fetchedAtMs <= ttlMs;
}

export function FirstRunProvider({ children }) {
  const [byUid, setByUid] = useState({});
  const inflightRef = useRef({});

  const readSession = useCallback((uid) => {
    try {
      const raw = sessionStorage.getItem(
        `${FIRST_RUN_SESSION_KEY_PREFIX}:${uid}`,
      );
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const writeSession = useCallback((uid, value) => {
    try {
      sessionStorage.setItem(
        `${FIRST_RUN_SESSION_KEY_PREFIX}:${uid}`,
        JSON.stringify(value),
      );
    } catch {
      // ignore
    }
  }, []);

  const clearAllFirstRun = useCallback(() => {
    setByUid({});
    try {
      const prefix = `${FIRST_RUN_SESSION_KEY_PREFIX}:`;
      for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith(prefix)) sessionStorage.removeItem(k);
      }
    } catch {
      // ignore
    }
  }, []);

  const getCachedFirstRun = useCallback(
    (uid) => {
      if (!uid) return null;
      if (byUid[uid]) return byUid[uid];
      return readSession(uid);
    },
    [byUid, readSession],
  );

  /**
   * ensureFirstRun
   * - returns { byVariant: Record<string, boolean>, firstRunAny: boolean }
   * - where byVariant[v] = true means: first run for that variant (no measurement)
   */
  const ensureFirstRun = useCallback(
    async ({
      db,
      uid,
      variants,
      ttlMs = FIRST_RUN_TTL_MS,
      force = false,
      requireCompleted = false,
    }) => {
      if (!uid) throw new Error("ensureFirstRun: uid is required");
      if (!db) throw new Error("ensureFirstRun: db is required");

      const existing = getCachedFirstRun(uid);

      const cachedByVariant = existing?.value?.byVariant || null;
      const cacheCoversRequested =
        cachedByVariant &&
        Array.isArray(variants) &&
        variants.every((v) =>
          Object.prototype.hasOwnProperty.call(cachedByVariant, v),
        );

      if (
        !force &&
        existing?.status === "ready" &&
        isFresh(existing, ttlMs) &&
        cacheCoversRequested
      ) {
        return existing.value;
      }

      if (inflightRef.current[uid]) return inflightRef.current[uid];

      setByUid((prev) => ({
        ...prev,
        [uid]: {
          status: "loading",
          value: prev[uid]?.value ?? null,
          error: null,
          fetchedAtMs: prev[uid]?.fetchedAtMs ?? null,
        },
      }));

      inflightRef.current[uid] = (async () => {
        try {
          const res = await fetchMeasurementVariantsPresence({
            db,
            uid,
            variants,
            requireCompleted,
          });

          const hasByVariant = res?.byVariant || {};

          // Ensure every requested variant exists in the map (default: "no measurement yet")
          const byVariant = Object.fromEntries(
            (variants || []).map((v) => [v, !(hasByVariant?.[v] === true)]),
          );

          const firstRunAny = Object.values(byVariant).some(Boolean);

          const value = { byVariant, firstRunAny };

          const entry = {
            status: "ready",
            value,
            error: null,
            fetchedAtMs: Date.now(),
          };

          setByUid((prev) => ({ ...prev, [uid]: entry }));
          writeSession(uid, entry);
          return value;
        } catch (e) {
          const msg = e?.message ? String(e.message) : "Unknown error";

          // safest default: gate everything
          const safeByVariant = Array.isArray(variants)
            ? Object.fromEntries(variants.map((v) => [v, true]))
            : {};

          const entry = {
            status: "error",
            value: { byVariant: safeByVariant, firstRunAny: true },
            error: msg,
            fetchedAtMs: Date.now(),
          };

          setByUid((prev) => ({ ...prev, [uid]: entry }));
          writeSession(uid, entry);
          return entry.value;
        } finally {
          delete inflightRef.current[uid];
        }
      })();

      return inflightRef.current[uid];
    },
    [getCachedFirstRun, writeSession],
  );

  const value = useMemo(
    () => ({ getCachedFirstRun, ensureFirstRun, clearAllFirstRun }),
    [getCachedFirstRun, ensureFirstRun, clearAllFirstRun],
  );

  return (
    <FirstRunContext.Provider value={value}>
      {children}
    </FirstRunContext.Provider>
  );
}

export function useFirstRun() {
  const ctx = useContext(FirstRunContext);
  if (!ctx) throw new Error("useFirstRun must be used within FirstRunProvider");
  return ctx;
}
